/**
 * TaskScheduler - Shared utility for optimal task scheduling
 * Replaces setTimeout with more efficient alternatives
 */

import { getEventScope } from "./event-scope/event-scope";

type TaskScheduleStrategy = "raf" | "dom" | "immediate";
type TaskResultValue = void | string | number | boolean | null | undefined | object;
type TaskFn<T = TaskResultValue> = () => T | Promise<T>;
type ScheduledTask<T = TaskResultValue> = TaskFn<T> | { fn: TaskFn<T>; strategy?: TaskScheduleStrategy };
type DebouncedResolve = (value: TaskResultValue | null) => void;

class TaskScheduler {
    private static _debounceTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

    private static _debouncedPromises: Record<string, Promise<TaskResultValue | null>> = {};

    private static _debouncedResolvers: Record<string, DebouncedResolve> = {};

    private static _domReadyPromise: Promise<void> | null = TaskScheduler.createDomReadyPromiseSlot();

    public static createDomReadyPromiseSlot(): Promise<void> | null {
        return null;
    }

    public static schedule<T>(taskFn: TaskFn<T>, strategy: TaskScheduleStrategy = "raf"): Promise<T | null> {
        switch (strategy) {
            case "immediate":
                return TaskScheduler.scheduleImmediate(taskFn);
            case "dom":
                return TaskScheduler.scheduleDOMReady(taskFn);
            case "raf":
            default:
                return TaskScheduler.scheduleRAF(taskFn);
        }
    }

    public static scheduleRAF<T>(taskFn: TaskFn<T>): Promise<T | null> {
        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(async () => {
                    try {
                        const result = await taskFn();
                        resolve(result);
                    } catch (error) {
                        console.error(error);
                        resolve(null);
                    }
                });
            });
        });
    }

    public static scheduleDOMReady<T>(taskFn: TaskFn<T>): Promise<T | null> {
        if (document.readyState !== "loading") {
            return TaskScheduler.scheduleRAF(taskFn);
        }

        return new Promise((resolve) => {
            const executeTask = async (): Promise<void> => {
                try {
                    const result = await TaskScheduler.scheduleRAF(taskFn);
                    resolve(result);
                } catch (error) {
                    console.error(error);
                    resolve(null);
                }
            };

            if (!TaskScheduler._domReadyPromise) {
                const scope = getEventScope(document, "task-scheduler");
                TaskScheduler._domReadyPromise = new Promise((domResolve) => {
                    scope.once("dom-ready", () => {
                        scope.on("DOMContentLoaded", () => domResolve(), { once: true });
                    });
                });
            }

            void TaskScheduler._domReadyPromise.then(executeTask);
        });
    }

    public static async scheduleImmediate<T>(taskFn: TaskFn<T>): Promise<T | null> {
        try {
            return await taskFn();
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    public static async scheduleAll(tasks: ScheduledTask[]): Promise<Array<TaskResultValue | null>> {
        const scheduledTasks = tasks.map((task) => {
            if (typeof task === "function") {
                return TaskScheduler.schedule(task);
            }
            if (task && typeof task.fn === "function") {
                return TaskScheduler.schedule(task.fn, task.strategy || "raf");
            }
            return Promise.resolve(null);
        });

        return Promise.all(scheduledTasks);
    }

    public static async scheduleSequence(tasks: TaskFn[]): Promise<Array<TaskResultValue | null>> {
        const results: Array<TaskResultValue | null> = [];
        for (const task of tasks) {
            const result = await TaskScheduler.schedule(task);
            results.push(result);
        }
        return results;
    }

    public static scheduleDebounced(taskFn: TaskFn, delay = 100, key = "default"): Promise<TaskResultValue | null> {
        if (TaskScheduler._debounceTimeouts[key]) {
            clearTimeout(TaskScheduler._debounceTimeouts[key]);
        }

        const scheduleTimeout = (): void => {
            TaskScheduler._debounceTimeouts[key] = setTimeout(async () => {
                try {
                    const result = await TaskScheduler.schedule(taskFn);
                    const resolve = TaskScheduler._debouncedResolvers[key];
                    delete TaskScheduler._debouncedPromises[key];
                    delete TaskScheduler._debouncedResolvers[key];
                    if (typeof resolve === "function") resolve(result);
                } catch (error) {
                    console.error(error);
                    const resolve = TaskScheduler._debouncedResolvers[key];
                    delete TaskScheduler._debouncedPromises[key];
                    delete TaskScheduler._debouncedResolvers[key];
                    if (typeof resolve === "function") resolve(null);
                }
            }, delay);
        };

        if (TaskScheduler._debouncedPromises[key]) {
            scheduleTimeout();
            return TaskScheduler._debouncedPromises[key];
        }

        const promise = new Promise<TaskResultValue | null>((resolve) => {
            TaskScheduler._debouncedResolvers[key] = resolve;
            scheduleTimeout();
        });

        TaskScheduler._debouncedPromises[key] = promise;
        return promise;
    }

    public static replacementSetTimeout(callback: TaskFn, delay = 0): Promise<TaskResultValue | null> {
        if (delay > 50) {
            return new Promise((resolve) => {
                setTimeout(async () => {
                    try {
                        const result = await callback();
                        resolve(result);
                    } catch (error) {
                        console.error(error);
                        resolve(null);
                    }
                }, delay);
            });
        }

        return TaskScheduler.scheduleRAF(callback);
    }
}

export { TaskScheduler };
